import { useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGetEconomySettingsChartOfAccountsQuery } from '@client/api';
import { Button, FormField, Input, Select, Skeleton, Table, TableBody, TableCell, TableRow } from '@client/toolkit';

import { EconomyActiveField } from './economy-active-field.component';
import { EconomyCardLoadError } from './economy-card-load-error.component';
import { EconomyCard } from './economy-card.component';
import { EconomyEditableCard } from './economy-editable-card.component';
import { EconomyRowActions } from './economy-row-actions.component';
import { EconomyTableHead } from './economy-table-head.component';
import { ECONOMY_FIELD_SIZE, ECONOMY_HINT_CLASS, ECONOMY_VALUE_CLASS } from '../economy-theme';
import {
  createEmptyOwnerCostAccount,
  OwnerCostAccountsFormPath,
  OwnerCostAccountsFormValues as FormValues,
  ownerCostAccountsSchema,
  toOwnerCostAccountsFormValues,
} from '../portfolio-economy-settings.form';

const COLUMNS = [
  { key: 'account', className: 'w-[150px]' },
  { key: 'name', className: 'w-[280px]' },
  { key: 'category', className: 'w-[300px]' },
  { key: 'active', className: 'w-[130px]' },
  { key: 'actions', className: 'w-[120px]', align: 'right' as const },
] as const;

/** The tenant's own eierkost categories - the klassifisering select's options. */
interface OwnerCostCategory {
  id: string;
  name: string;
}

interface OwnerCostRowProps {
  index: number;
  editing: boolean;
  isFirst: boolean;
  isLast: boolean;
  categories: readonly OwnerCostCategory[];
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onRemove: (index: number) => void;
}

function OwnerCostAccountRow({
  index,
  editing,
  isFirst,
  isLast,
  categories,
  onMoveUp,
  onMoveDown,
  onRemove,
}: OwnerCostRowProps) {
  const { t } = useTranslation('economy');
  const { control, trigger, getFieldState, formState } = useFormContext<FormValues>();

  const account = useWatch({ control, name: `ownerCostAccounts.${index}` });
  const accountNumber = account?.accountNumber;
  const row = typeof accountNumber === 'string' && accountNumber.trim() !== '' ? accountNumber : String(index + 1);

  const revalidateAccountNumbers = () => {
    if (getFieldState('ownerCostAccounts', formState).error != null) void trigger('ownerCostAccounts');
  };

  return (
    <TableRow verticalAlign="middle">
      <TableCell>
        {!editing ? (
          <span className={ECONOMY_VALUE_CLASS}>{accountNumber}</span>
        ) : (
          <FormField<FormValues, OwnerCostAccountsFormPath>
            name={`ownerCostAccounts.${index}.accountNumber`}
            render={({ field }) => (
              <Input
                {...field}
                onChange={(event) => {
                  field.onChange(event);
                  revalidateAccountNumbers();
                }}
                size={ECONOMY_FIELD_SIZE}
                aria-label={t('settings.ownerCostAccounts.rowFields.account', { row })}
              />
            )}
          />
        )}
      </TableCell>

      <TableCell>
        {!editing ? (
          <span className={ECONOMY_VALUE_CLASS}>{account?.name}</span>
        ) : (
          <FormField<FormValues, OwnerCostAccountsFormPath>
            name={`ownerCostAccounts.${index}.name`}
            render={({ field }) => (
              <Input
                {...field}
                size={ECONOMY_FIELD_SIZE}
                aria-label={t('settings.ownerCostAccounts.rowFields.name', { row })}
              />
            )}
          />
        )}
      </TableCell>

      <TableCell>
        {!editing ? (
          <span className={ECONOMY_VALUE_CLASS}>
            {categories.find((category) => category.id === account?.costCategoryId)?.name}
          </span>
        ) : (
          <FormField<FormValues, OwnerCostAccountsFormPath>
            name={`ownerCostAccounts.${index}.costCategoryId`}
            render={({ field }) => (
              <Select
                {...field}
                size={ECONOMY_FIELD_SIZE}
                aria-label={t('settings.ownerCostAccounts.rowFields.category', { row })}
              >
                {/* Every category the tenant has, including ones no account uses yet - which is why
                    the API sends this list separately rather than leaving it to be derived from the
                    accounts. Names are free text from the database, so no i18n key applies. */}
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
            )}
          />
        )}
      </TableCell>

      <TableCell>
        <EconomyActiveField<FormValues>
          section="settings.ownerCostAccounts"
          row={row}
          name={`ownerCostAccounts.${index}.active`}
          active={account?.active}
          editing={editing}
        />
      </TableCell>

      <TableCell align="right">
        {editing && (
          <EconomyRowActions
            section="settings.ownerCostAccounts"
            row={row}
            isFirst={isFirst}
            isLast={isLast}
            onMoveUp={() => onMoveUp(index)}
            onMoveDown={() => onMoveDown(index)}
            onRemove={() => onRemove(index)}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

/** Reads the form the card owns, so it has to live inside its `FormProvider`. */
function OwnerCostAccountsTable({
  editing,
  categories,
}: {
  editing: boolean;
  categories: readonly OwnerCostCategory[];
}) {
  const { t } = useTranslation('economy');
  const { control, trigger, getFieldState, formState } = useFormContext<FormValues>();

  const { fields, remove, swap } = useFieldArray<FormValues, 'ownerCostAccounts'>({
    control,
    name: 'ownerCostAccounts',
  });

  const revalidateAfter = (mutate: () => void) => {
    const hadError = getFieldState('ownerCostAccounts', formState).error != null;
    mutate();
    if (hadError) void trigger('ownerCostAccounts');
  };

  return (
    <>
      {/* No `size`: the toolkit `Table` never puts it on `TableContext`, so the cells fall
          back to `medium` whatever is passed. Asking for `xs` here only reads as if the
          tables were compact. */}
      <Table className="w-full rounded-md">
        <EconomyTableHead section="settings.ownerCostAccounts" columns={COLUMNS} />
        <TableBody>
          {fields.map((field, index) => (
            <OwnerCostAccountRow
              key={field.id}
              index={index}
              editing={editing}
              categories={categories}
              isFirst={index === 0}
              isLast={index === fields.length - 1}
              onMoveUp={(from) => revalidateAfter(() => swap(from, from - 1))}
              onMoveDown={(from) => revalidateAfter(() => swap(from, from + 1))}
              onRemove={(at) => revalidateAfter(() => remove(at))}
            />
          ))}
        </TableBody>
      </Table>

      {fields.length === 0 && <p className="py-4 text-sm text-secondary">{t('settings.ownerCostAccounts.empty')}</p>}

      <p className={`mt-3 px-3 ${ECONOMY_HINT_CLASS}`}>{t('settings.ownerCostAccounts.hint')}</p>
    </>
  );
}

/** Header action; writes through the form so the table's field array picks the row up. */
function AddOwnerCostAccountButton({ categories }: { categories: readonly OwnerCostCategory[] }) {
  const { t } = useTranslation('economy');
  const { getValues, setValue } = useFormContext<FormValues>();

  return (
    <Button
      type="button"
      color="primary-dark"
      size="sm"
      // An account must belong to a category, and the categories come from the server. With none
      // there is nothing valid to create.
      disabled={categories.length === 0}
      onClick={() =>
        setValue(
          'ownerCostAccounts',
          [...getValues('ownerCostAccounts'), createEmptyOwnerCostAccount(categories[0].id)],
          { shouldDirty: true },
        )
      }
    >
      {t('settings.ownerCostAccounts.add')}
    </Button>
  );
}

export interface OwnerCostAccountsCardProps {
  /**
   * Omitted for now, which renders the card read-only. Wire it when EXIO-2988 ships the PUT:
   * EconomyEditableCard reports success unconditionally, so a card that cannot persist must not
   * offer Rediger. Raised in review on PR #625.
   */
  onSave?: (values: FormValues) => void;
}

/**
 * Portfolio-wide eierkost defaults a new building budget is pre-populated from.
 *
 * The card fetches its own data, as GroupCard does. Note that two of the three seeded categories
 * arrive with no accounts, so an empty table alongside a populated klassifisering select is the
 * normal state rather than a bug.
 */
export function OwnerCostAccountsCard({ onSave }: OwnerCostAccountsCardProps) {
  const { t } = useTranslation('economy');

  const { data, isLoading, isError } = useGetEconomySettingsChartOfAccountsQuery();

  const categories = useMemo(
    () => (data?.ownerCost.categories ?? []).map(({ id, name }) => ({ id, name })),
    [data],
  );
  const defaultValues = useMemo(() => toOwnerCostAccountsFormValues(data), [data]);

  if (isLoading) {
    return (
      <EconomyCard title={t('settings.ownerCostAccounts.title')}>
        <div data-testid="owner-cost-accounts-skeleton">
          <Skeleton variant="text" height="h-4" width="w-64" />
        </div>
      </EconomyCard>
    );
  }

  // An empty chart and a failed request are different facts; rendering the first for the second
  // claims something we have not established.
  if (isError || data == null) {
    return (
      <EconomyCardLoadError
        title={t('settings.ownerCostAccounts.title')}
        message={t('settings.ownerCostAccounts.loadError')}
      />
    );
  }

  return (
    <EconomyEditableCard<FormValues>
      title={t('settings.ownerCostAccounts.title')}
      defaultValues={defaultValues}
      schema={ownerCostAccountsSchema}
      onSave={onSave}
      editActions={<AddOwnerCostAccountButton categories={categories} />}
    >
      {(editing) => <OwnerCostAccountsTable editing={editing} categories={categories} />}
    </EconomyEditableCard>
  );
}

export default OwnerCostAccountsCard;
